import {useEffect,useState} from 'react';
import {useQuery,useQueryClient} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {Button} from '@aiw/ui';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';

/**
 * The strata submission pack: one button on the Job (item 13 of Jacky's fourteen, "Strata application
 * support"). Everything comes from the Job's own records — the accepted Proposal or locked quote, the
 * saved drawing, the catalogue's documents for the supplied product, the company certificates on file —
 * so the panel shows what the pack WILL hold and what is not on file, then builds it. The finished PDF is
 * kept under the Job in "Prepared documents" and opened for review; nothing is sent from here.
 */
interface Plan {job_ref:string;source:'proposal'|'quote'|null;products:string[];underlay:string|null;documents:{kind:string;title:string;available:boolean;reason:string|null}[];missing:string[];next_revision:number;previous:{revision:number;prepared_at:string;prepared_by:string}[]}
interface Built {revision:number;pages:number;kept:boolean;path:string|null;keep_error:string|null;missing:string[];attached:string[]}
async function readPlan(ref:string):Promise<Plan>{
  if(env.mock)return {job_ref:ref,source:null,products:[],underlay:null,documents:[],missing:[],next_revision:1,previous:[]};
  const r=await fetchImpl(`${env.apiBase}/api/jobs/${encodeURIComponent(ref)}/strata-pack`,{headers:await authHeaders()});
  const body=await r.json() as {data?:Plan;error?:string};
  if(!r.ok||!body.data)throw Error(body.error??`api ${r.status}`);
  return body.data;
}

export function JobStrataPack({jobRef}:{jobRef:string}){
  const {t}=useT();
  const queryClient=useQueryClient();
  const plan=useQuery({queryKey:['job',jobRef,'strata-pack'],queryFn:()=>readPlan(jobRef),enabled:!!jobRef});
  const [busy,setBusy]=useState(false),[notice,setNotice]=useState<string|null>(null),[built,setBuilt]=useState<{url:string;name:string;info:Built}|null>(null);
  useEffect(()=>{setBuilt(null);setNotice(null);},[jobRef]);
  useEffect(()=>()=>{if(built)URL.revokeObjectURL(built.url)},[built]);

  async function prepare(){
    if(busy)return;
    setBusy(true);setNotice(null);
    try{
      const r=await fetchImpl(`${env.apiBase}/api/jobs/${encodeURIComponent(jobRef)}/strata-pack`,{method:'POST',headers:await authHeaders()});
      if(!r.ok){const body=await r.json().catch(()=>({})) as {error?:string};throw Error(body.error??t('job.strataFailed'));}
      const info=JSON.parse(r.headers.get('x-aiw-strata-pack')??'{}') as Built;
      const bytes=await r.arrayBuffer();
      const url=URL.createObjectURL(new Blob([bytes],{type:'application/pdf'}));
      const name=`Strata_Pack_${jobRef}_rev${String(info.revision??1).padStart(2,'0')}.pdf`;
      setBuilt({url,name,info});
      setNotice(info.kept?t('job.strataKept',{revision:String(info.revision),pages:String(info.pages)}):t('job.strataNotKept',{reason:info.keep_error??''}));
      await Promise.all([queryClient.invalidateQueries({queryKey:['job',jobRef,'strata-pack']}),queryClient.invalidateQueries({queryKey:['archive']})]);
    }catch(e){setNotice(e instanceof Error?e.message:t('job.strataFailed'));}
    finally{setBusy(false);}
  }

  const p=plan.data;
  return <section className="mt-3 border-t pt-3 text-sm" aria-label={t('job.strataTitle')}>
    <h3 className="font-semibold">{t('job.strataTitle')}</h3>
    <p className="mt-1 text-muted-foreground">{t('job.strataHint')}</p>
    {plan.isPending&&<p role="status" className="mt-2 text-muted-foreground">…</p>}
    {plan.isError&&<p role="alert" className="mt-2 text-destructive">{t('job.strataUnavailable')}</p>}
    {p&&<>
      <p className="mt-2"><span className="text-muted-foreground">{t('job.documentSuppliedProduct')}:</span> {p.products.join(', ')||<span className="text-muted-foreground">{t('job.documentProductUnknown')}</span>}{p.underlay?` · ${t('job.strataUnderlay')} ${p.underlay}`:''}</p>
      <ul className="mt-1">{p.documents.map(d=><li key={`${d.kind}:${d.title}`} className={d.available?'':'text-muted-foreground'}>{d.available?'✓':'–'} {d.title}{d.available?'':` — ${d.reason??''}`}</li>)}</ul>
      {p.missing.filter(m=>!p.documents.some(d=>!d.available&&m.startsWith(d.title))).map(m=><p key={m} className="mt-1 text-muted-foreground">{t('job.strataMissing')} {m}</p>)}
      {p.previous.length>0&&<p className="mt-1 text-muted-foreground">{t('job.strataPrevious',{count:String(p.previous.length),revision:String(p.previous.at(-1)!.revision)})}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy||plan.isPending} onClick={()=>void prepare()}>{t('job.strataPrepare',{revision:String(p.next_revision)})}</Button>
        {built&&<a href={built.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('job.previewDocument')} · {built.name}</a>}
        <Link to={`/archive?job=${encodeURIComponent(jobRef)}`} className="text-primary underline">{t('job.strataOpenDocuments')}</Link>
      </div>
      {notice&&<p role="status" className="mt-2">{notice}</p>}
    </>}
  </section>;
}
